/**
 * The Accounts console's own API surface.
 *
 * Everything here goes through `api.requestText`, so every amount is read out of
 * the raw response text as a digit string and written into a request body as a
 * JSON integer literal — no `JSON.parse` step ever produces a `Number` from an
 * amount (design D18). There is no fallback data of any kind: a failed request
 * surfaces as an `ApiRequestError`, never as an empty or sample page.
 */

import { api, ApiRequestError } from '@/lib/api'
import { parseJsonPreservingAmounts } from './bigintJson'
import { tenantHeaders } from './applicationContext'

/** Amount-bearing values arrive as digit strings, never as numbers. */
export interface SubAccountRow {
  id: string
  type: string
  asset_kind: string
  currency: string | null
  balance: string
  available: string
  pending: string
  status: string
}

export interface AccountRow {
  account_id: string
  user_id: number
  external_ref: string | null
  sub_accounts: SubAccountRow[]
  last_entry_at: string | null
  created_at: string
}

export interface AccountListEnvelope {
  accounts: AccountRow[]
  total: number
  page: number
  per_page: number
}

export interface OperatorLedgerRow {
  id: string
  sub_account_id: string
  sub_account_type: string
  account_id: string
  entry_type: string
  direction: string
  amount: string
  fee: string
  currency: string | null
  asset_kind: string
  status: string
  balance_before: string | null
  balance_after: string | null
  available_on: string | null
  expires_at: string | null
  reference_type: string | null
  reference_id: string | null
  payment_order_id: string | null
  description: string | null
  operator_id: number | null
  is_bonus_grant: boolean
  created_at: string
}

export interface OperatorLedgerEnvelope {
  entries: OperatorLedgerRow[]
  total: number
  page: number
  per_page: number
}

export interface ReversalOutcome {
  account_id: string
  requested: string
  total_reversed: string
  shortfall: string
}

export async function fetchAccountList(
  applicationId: number,
  params: Record<string, string | number>
): Promise<AccountListEnvelope> {
  const text = await api.requestText('GET', '/accounts/list', {
    params,
    headers: tenantHeaders(applicationId),
  })
  return parseJsonPreservingAmounts<AccountListEnvelope>(text)
}

export async function fetchOperatorLedger(
  applicationId: number,
  accountId: string,
  params: Record<string, string | number>
): Promise<OperatorLedgerEnvelope> {
  const text = await api.requestText(
    'GET',
    `/accounts/${encodeURIComponent(accountId)}/operator-ledger`,
    { params, headers: tenantHeaders(applicationId) }
  )
  return parseJsonPreservingAmounts<OperatorLedgerEnvelope>(text)
}

export interface GrantPayload {
  amount: string
  reason: string
  idempotencyKey: string
  /** Explicit-`Z` UTC instant, or null when the grant never expires. */
  expiresAt: string | null
}

/**
 * Serialize a grant body with the amount as a JSON integer **literal** built
 * from the operator's digits — never `JSON.stringify` over a `Number`.
 */
export function buildGrantBody(payload: GrantPayload): string {
  const fields = [
    `"amount": ${payload.amount}`,
    '"direction": "credit"',
    `"reason": ${JSON.stringify(payload.reason)}`,
    `"idempotency_key": ${JSON.stringify(payload.idempotencyKey)}`,
    '"is_bonus_grant": true',
  ]
  if (payload.expiresAt != null) {
    fields.push(`"expires_at": ${JSON.stringify(payload.expiresAt)}`)
  }
  return `{${fields.join(', ')}}`
}

export interface ReversalPayload {
  amount: string
  reason: string
  idempotencyKey: string
}

export function buildReversalBody(payload: ReversalPayload): string {
  return `{${[
    `"amount": ${payload.amount}`,
    `"reason": ${JSON.stringify(payload.reason)}`,
    `"idempotency_key": ${JSON.stringify(payload.idempotencyKey)}`,
  ].join(', ')}}`
}

export async function submitGrant(
  applicationId: number,
  accountId: string,
  subAccountId: string,
  payload: GrantPayload
): Promise<void> {
  await api.requestText(
    'POST',
    `/accounts/${encodeURIComponent(accountId)}/sub-accounts/${encodeURIComponent(
      subAccountId
    )}/adjust`,
    { bodyText: buildGrantBody(payload), headers: tenantHeaders(applicationId) }
  )
}

export async function submitReversal(
  applicationId: number,
  accountId: string,
  payload: ReversalPayload
): Promise<ReversalOutcome> {
  const text = await api.requestText(
    'POST',
    `/accounts/${encodeURIComponent(accountId)}/reverse-grant`,
    { bodyText: buildReversalBody(payload), headers: tenantHeaders(applicationId) }
  )
  return parseJsonPreservingAmounts<ReversalOutcome>(text)
}

/**
 * One entry of the tenant's bucket type catalogue — of **every** asset kind
 * (grant-dialog-monetary-bonus-target, design D1). `currency` is an ISO 4217
 * code for a monetary type and `null` for a virtual one, both as stored;
 * `withdrawable` is the type config's own column, which the reclaim-offer
 * predicate reads.
 */
export interface SubAccountTypeRow {
  type_key: string
  display_name: string
  asset_kind: string
  currency: string | null
  app_creditable: boolean
  spending_priority: number
  withdrawable: boolean
}

export interface SubAccountTypeEnvelope {
  sub_account_types: SubAccountTypeRow[]
}

/**
 * `GET /accounts/sub-account-types` — the admin-only, tenant-scoped listing of
 * every asset kind. The grant form reads its targets' `spending_priority` and
 * `withdrawable` from it, and the provisioning step chooses from it. A failed
 * request throws, and is rendered as the failure it is; never an empty
 * catalogue.
 */
export async function fetchSubAccountTypes(
  applicationId: number
): Promise<SubAccountTypeEnvelope> {
  const text = await api.requestText('GET', '/accounts/sub-account-types', {
    headers: tenantHeaders(applicationId),
  })
  return parseJsonPreservingAmounts<SubAccountTypeEnvelope>(text)
}

export interface ProvisionPayload {
  typeKey: string
  displayName: string
  /** The selected type config's own asset kind — never a constant. */
  assetKind: string
  /** The type config's own currency; sent for a monetary type, omitted else. */
  currency?: string | null
}

/**
 * The provisioning body carries the selected type's own fields and nothing
 * else: the type's `asset_kind` as the catalogue reported it, and — for a
 * monetary type only — that type's own `currency` (the operator is offered no
 * currency choice: the currency is a property of the type config). No
 * `spending_priority`, which the endpoint ignores on a matched type and which
 * this dialog therefore displays but never sends; no `app_creditable`, which
 * the endpoint never updates on a matched config; and no amount, reason,
 * expiry or idempotency key — those belong to the grant alone.
 */
export function buildProvisionBody(payload: ProvisionPayload): string {
  const fields = [
    `"type_key": ${JSON.stringify(payload.typeKey)}`,
    `"asset_kind": ${JSON.stringify(payload.assetKind)}`,
    `"display_name": ${JSON.stringify(payload.displayName)}`,
  ]
  if (payload.assetKind === 'monetary' && payload.currency != null) {
    fields.push(`"currency": ${JSON.stringify(payload.currency)}`)
  }
  return `{${fields.join(', ')}}`
}

export interface DirectedDebitPayload {
  amount: string
  reason: string
  idempotencyKey: string
}

/**
 * The directed monetary reclaim body (design D7). Deliberately NOT a grant: no
 * `is_bonus_grant` (that marks a credit) and no `expires_at` (the server
 * rejects one on a debit). The amount is the minor-unit integer, written as a
 * JSON integer literal from digits, exactly as every other amount here.
 */
export function buildDirectedDebitBody(payload: DirectedDebitPayload): string {
  return `{${[
    `"amount": ${payload.amount}`,
    '"direction": "debit"',
    `"reason": ${JSON.stringify(payload.reason)}`,
    `"idempotency_key": ${JSON.stringify(payload.idempotencyKey)}`,
  ].join(', ')}}`
}

/**
 * `POST /accounts/<id>/sub-accounts/<sub_id>/adjust` with `direction: "debit"`
 * — the only path by which a monetary bonus can be reclaimed. All-or-nothing:
 * a `422 insufficient_balance` debits nothing and is a determinate refusal.
 */
export async function submitDirectedDebit(
  applicationId: number,
  accountId: string,
  subAccountId: string,
  payload: DirectedDebitPayload
): Promise<void> {
  await api.requestText(
    'POST',
    `/accounts/${encodeURIComponent(accountId)}/sub-accounts/${encodeURIComponent(
      subAccountId
    )}/adjust`,
    {
      bodyText: buildDirectedDebitBody(payload),
      headers: tenantHeaders(applicationId),
    }
  )
}

/**
 * `POST /accounts/<id>/sub-accounts` — the shipped, unchanged provisioning
 * endpoint. Its response body is deliberately not returned: nothing the console
 * renders may be inferred from it (design D6), only from the reloaded row.
 */
export async function submitProvisionSubAccount(
  applicationId: number,
  accountId: string,
  payload: ProvisionPayload
): Promise<void> {
  await api.requestText(
    'POST',
    `/accounts/${encodeURIComponent(accountId)}/sub-accounts`,
    {
      bodyText: buildProvisionBody(payload),
      headers: tenantHeaders(applicationId),
    }
  )
}

/** The server's own message for a failed request — never a predicted one. */
export function serverErrorMessage(err: unknown, fallback: string): string {
  return err instanceof ApiRequestError ? err.error.message : fallback
}

export function errorStatus(err: unknown): number | null {
  return err instanceof ApiRequestError ? err.status : null
}

/** The server's own error code, for the few places that branch on one. */
export function errorCode(err: unknown): string | null {
  return err instanceof ApiRequestError ? err.error.code ?? null : null
}
